import { useEffect, useRef, useState } from 'react'

/**
 * A countdown that reports remaining time in tenths.
 *
 * It tracks against a wall-clock deadline rather than accumulating interval
 * ticks, so a backgrounded tab or a slow frame can't hand you extra seconds —
 * which would quietly defeat the point of a pressure drill.
 */
export function useCountdown(seconds: number, running: boolean, onExpire?: () => void) {
  const [remaining, setRemaining] = useState(seconds)
  const expireRef = useRef(onExpire)
  expireRef.current = onExpire

  useEffect(() => {
    if (!running) {
      setRemaining(seconds)
      return
    }

    const deadline = Date.now() + seconds * 1000
    let fired = false

    const tick = () => {
      const left = Math.max(0, (deadline - Date.now()) / 1000)
      setRemaining(left)
      if (left <= 0 && !fired) {
        fired = true
        expireRef.current?.()
      }
    }

    tick()
    const id = window.setInterval(tick, 100)
    return () => window.clearInterval(id)
  }, [seconds, running])

  return remaining
}

export function Countdown({ remaining, total }: { remaining: number; total: number }) {
  const urgent = remaining <= Math.min(5, total * 0.3)
  const pct = total === 0 ? 0 : Math.max(0, Math.min(100, (remaining / total) * 100))

  return (
    <div>
      <div
        className={`timer ${urgent ? 'is-urgent' : ''}`}
        role="timer"
        aria-live="off"
      >
        {remaining.toFixed(1)}s
      </div>
      <div className="timer-track">
        <div
          className={`timer-fill ${urgent ? 'is-urgent' : ''}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}
